/**
 * Wiring: socket (or the dev mock) → scene + UI.
 */
import './style.css';
import type { Villager } from '@village/shared';
import { Net } from './net';
import { VillageScene } from './scene';
import { UI } from './ui';

const params = new URLSearchParams(location.search);
const wantMock = params.get('mock') === '1';
/** `?select=<id>` or `?select=first` preselects a villager — handy for debugging and screenshots. */
const wantSelect = params.get('select');

const canvas = document.getElementById('stage') as HTMLCanvasElement;
const boot = document.getElementById('boot');
const scene = new VillageScene(canvas);
const ui = new UI();

const villagers = new Map<string, Villager>();
let mockStarted = false;

const net = new Net({
  giveUpAfter: 3,
  onGiveUp: () => {
    if (import.meta.env.DEV) startMock('the socket kept failing');
  },
});

async function startMock(reason: string): Promise<void> {
  if (mockStarted) return;
  mockStarted = true;
  net.stop();
  console.info(`[village] using the dev mock — ${reason}`);
  const { startMock: run } = await import('./dev/mock');
  run(net);
}

function selectVillager(id: string | null, recenter: boolean): void {
  ui.select(id);
  scene.select(ui.selectedId);
  if (recenter && ui.selectedId) {
    const v = villagers.get(ui.selectedId);
    if (v) scene.lookAt(v);
  }
}

net.on('status', (status) => ui.setStatus(status));

net.on('hello', (hello) => {
  villagers.clear();
  for (const v of hello.villagers) villagers.set(v.id, v);
  scene.setWorld(hello);
  ui.setHello(hello);
  if (wantSelect && !ui.selectedId) {
    const id = wantSelect === 'first' ? hello.villagers[0]?.id : wantSelect;
    if (id) selectVillager(id, false);
  }
  scene.select(ui.selectedId);
  boot?.classList.add('is-gone');
});

net.on('tick', (tick) => {
  for (const v of tick.villagers) villagers.set(v.id, v);
  scene.onTick(tick);
  ui.setTick(tick);
});

scene.onSelect = (picked) => {
  if (!picked) {
    selectVillager(null, false);
    return;
  }
  if (picked.villagerId) selectVillager(picked.villagerId, false);
};

ui.onSelect = (id) => selectVillager(id === ui.selectedId ? null : id, true);

if (wantMock) void startMock('?mock=1');
else net.connect();
